import { create } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useDebounce from "react-use/lib/useDebounce";
import { tickerServiceClient } from "@/connect";
import { CACHE_CONFIG } from "@/lib/react-query";
import type { ListTickersRequest, Ticker } from "@/types/proto/api/v1/ticker_service_pb";
import { ListTickerSymbolsRequestSchema, ListTickersRequestSchema, TickerSchema } from "@/types/proto/api/v1/ticker_service_pb";

// Query keys factory for consistent cache management
export const tickerKeys = {
  all: ["tickers"] as const,
  lists: () => [...tickerKeys.all, "list"] as const,
  list: (filters: Partial<ListTickersRequest>) => [...tickerKeys.lists(), filters] as const,
  details: () => [...tickerKeys.all, "detail"] as const,
  detail: (symbol: string) => [...tickerKeys.details(), symbol] as const,
};

/**
 * Hook to fetch tickers list
 * @param request - Optional filters for the list request
 */
export function useTickers(request: Partial<ListTickersRequest> = {}) {
  return useQuery({
    queryKey: tickerKeys.list(request),
    queryFn: async () => {
      const response = await tickerServiceClient.listTickers(create(ListTickersRequestSchema, request as Record<string, unknown>));
      return response;
    },
    ...CACHE_CONFIG.MODERATE, // ticker 是半静态数据
  });
}

/**
 * Hook to fetch a single ticker by symbol
 * @param symbol - The ticker symbol (e.g., "TSLA")
 * @param options - Query options like enabled flag
 */
export function useTicker(symbol: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: tickerKeys.detail(symbol),
    queryFn: async () => {
      const response = await tickerServiceClient.getTicker({
        name: `tickers/${symbol}`,
      });
      return response;
    },
    enabled: options?.enabled ?? !!symbol,
    ...CACHE_CONFIG.MODERATE,
  });
}

/**
 * Hook to create a new ticker
 * Automatically fetches info from FMP API if ticker doesn't exist
 */
export function useCreateTicker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (symbol: string) => {
      const ticker = await tickerServiceClient.createTicker({
        ticker: create(TickerSchema, { symbol }),
      });
      return ticker;
    },
    onSuccess: (newTicker) => {
      // Invalidate ALL ticker queries (including those with filters)
      // v0.71.0: Use tickerKeys.all to ensure filtered queries are also invalidated
      queryClient.invalidateQueries({ queryKey: tickerKeys.all });
      // Invalidate ALL watchlist ticker queries (new ticker may be added to watchlists)
      queryClient.invalidateQueries({ queryKey: ["watchlists", "tickers"] });
      // Add new ticker to cache
      queryClient.setQueryData(tickerKeys.detail(newTicker.symbol), {
        ticker: newTicker,
      });
    },
  });
}

/**
 * Hook to update a ticker
 */
export function useUpdateTicker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticker, updateMask }: { ticker: Partial<Ticker>; updateMask: string[] }) => {
      const updated = await tickerServiceClient.updateTicker({
        ticker: create(TickerSchema, ticker as Record<string, unknown>),
        updateMask: { paths: updateMask },
      });
      return updated;
    },
    onSuccess: (updatedTicker) => {
      // Update cache by merging only the status field
      // UpdateTicker response only contains updated fields, not memos or other data
      queryClient.setQueryData(tickerKeys.detail(updatedTicker.symbol), (oldData: unknown) => {
        const data = oldData as { ticker?: { symbol?: string; status?: string } } | undefined;
        if (!data?.ticker) {
          return { ticker: updatedTicker };
        }
        return {
          ...data,
          ticker: {
            ...data.ticker,
            status: updatedTicker.status,
          },
        };
      });
      // Invalidate ticker lists to update other views (ticker list page, etc.)
      // But don't invalidate the current ticker detail since we already updated it above
      queryClient.invalidateQueries({ queryKey: tickerKeys.lists() });
      // Invalidate watchlist ticker queries
      queryClient.invalidateQueries({ queryKey: ["watchlists", "tickers"] });
    },
  });
}

/**
 * Hook to delete a ticker
 */
export function useDeleteTicker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (symbol: string) => {
      await tickerServiceClient.deleteTicker({
        name: `tickers/${symbol}`,
      });
      return symbol;
    },
    onSuccess: (symbol) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: tickerKeys.detail(symbol) });
      // Invalidate ALL ticker lists (including filtered queries)
      // v0.71.0: Use tickerKeys.all to ensure filtered queries are also invalidated
      queryClient.invalidateQueries({ queryKey: tickerKeys.all });
      // Invalidate ALL watchlist ticker queries (ticker may belong to multiple watchlists)
      queryClient.invalidateQueries({ queryKey: ["watchlists", "tickers"] });
    },
  });
}

// Query keys for ticker prices
export const tickerPriceKeys = {
  all: ["tickerPrices"] as const,
  details: () => [...tickerPriceKeys.all, "detail"] as const,
  detail: (symbol: string, timeRange: string) => [...tickerPriceKeys.details(), symbol, timeRange] as const,
};

/**
 * Hook to search tickers by symbol or name (for auto-completion)
 * @param query - Search query (symbol or company name)
 * @param options - Query options like enabled flag
 */
export function useSearchTickers(query: string, options?: { enabled?: boolean }) {
  // Debounce query to avoid excessive API calls (300ms delay)
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useDebounce(
    () => {
      setDebouncedQuery(query);
    },
    300,
    [query],
  );

  return useQuery({
    queryKey: [...tickerKeys.all, "search", debouncedQuery] as const,
    queryFn: async () => {
      const response = await tickerServiceClient.searchTickers({
        query: debouncedQuery,
        limit: 10,
      });
      return response;
    },
    enabled: (options?.enabled ?? false) && debouncedQuery.length >= 2,
    ...CACHE_CONFIG.STATIC, // ticker master 是静态数据
  });
}

/**
 * Hook to fetch lightweight ticker symbols for auto-completion
 * v0.71.0: Lightweight endpoint for editor suggestions (symbol + name only)
 * @param options - Query options like userTickersOnly flag and enabled flag
 */
export function useTickerSymbols(options?: { userTickersOnly?: boolean; enabled?: boolean }) {
  return useQuery({
    queryKey: [...tickerKeys.all, "symbols", options?.userTickersOnly ?? false] as const,
    queryFn: async () => {
      const response = await tickerServiceClient.listTickerSymbols(
        create(ListTickerSymbolsRequestSchema, {
          userTickersOnly: options?.userTickersOnly ?? false,
          limit: 500, // Default: load up to 500 symbols
        } as Record<string, unknown>),
      );
      return response;
    },
    enabled: options?.enabled ?? true,
    ...CACHE_CONFIG.STATIC, // ticker symbols 是半静态数据
  });
}

/**
 * Hook to fetch historical ticker prices with time range filtering
 * @param symbol - The ticker symbol (e.g., "AAPL")
 * @param timeRange - Time range for prices: "1m", "3m", "6m", "YTD", "1y", "3y", "5y" (default: "1y")
 * @param options - Query options like enabled flag
 */
export function useTickerPrice(symbol: string, timeRange: string = "1y", options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: tickerPriceKeys.detail(symbol, timeRange),
    queryFn: async () => {
      const response = await tickerServiceClient.getTickerPrice({
        symbol,
        timeRange,
      });
      return response;
    },
    enabled: options?.enabled ?? !!symbol,
    ...CACHE_CONFIG.REALTIME, // 价格是实时数据
  });
}

// Query keys for market holidays
export const holidayKeys = {
  all: ["holidays"] as const,
  lists: () => [...holidayKeys.all, "list"] as const,
  list: (market: string, year: number) => [...holidayKeys.lists(), market, year] as const,
};

/**
 * Hook to fetch market holidays
 * @param market - Market identifier (default: "US")
 * @param year - Year to fetch holidays for (default: current year)
 * @param options - Query options like enabled flag
 */
export function useHolidays(market: string = "US", year?: number, options?: { enabled?: boolean }) {
  const currentYear = year ?? new Date().getFullYear();
  return useQuery({
    queryKey: holidayKeys.list(market, currentYear),
    queryFn: async () => {
      const response = await tickerServiceClient.getHolidays({
        market,
        year: currentYear,
      });
      return response;
    },
    enabled: options?.enabled ?? true,
    ...CACHE_CONFIG.STATIC, // 节假日是静态数据
  });
}
